<template>
  <component
    :is="dynamicComponent"
    ref="workTableDialog"
    :content-class="`w-table-dialog ${contentClass}`"
    :width="width"
    :height="height"
    :fixed-height="fixedHeight"
    :show-actions="filledActionSlot"
    v-bind="filterProps($props, 'title')"
    :title="computedTitle"
  >
    <template #header>
      <slot name="header" />
    </template>
    <template #content>
      <div
        :class="
          !$slots['cart', 'left-prepend']
            ? ' w-table-dialog__table-wrapper '
            : ' d-flex w-table-dialog__content-wrapper '
        "
      > 
        <w-container
          v-if="$slots['left-prepend']"
          justify="center"
          class="workTableDialog__left-prepend"
        >
          <slot name="left-prepend" />
        </w-container>
        <div class="w-table-dialog__table-wrapper">
          <div v-if="$slots['append-table-content-above']">
            <w-container
              justify="center"
              class="workTableDialog__search-filters"
            >
              <slot name="append-table-content-above" />
            </w-container>
          </div>

          <v-expansion-panels
            v-if="showFilter"
            v-model="collapsePanel"
            flat
          >
            <v-expansion-panel>
              <w-expansion-panel-header class="w-expansion-panel-header">
                <span>{{ $t('Common.filter&GroupBy') }}</span>
              </w-expansion-panel-header>
              <w-expansion-panel-content class="w-expansion-panel-content">
                <WorkDialogSearchFilterWrapper v-model="filters">
                  <w-row>
                    <w-column v-if="showSearchFilter">
                      <w-text-field
                        ref="searchExpressionFilter"
                        append-icon="$wSearch"
                        :label="$t('Common.search')"
                        searchKey="search"
                        single-line
                        hide-details
                        outlined
                        dense
                        clearable
                        :spellcheck="false"
                      />
                    </w-column> 
                    <slot name="append-search-expression-filter" />
                  </w-row>
                  <slot
                    name="search-filters"
                    :filters-data="computedFilters"
                  />
                </WorkDialogSearchFilterWrapper>
              </w-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div
            :class="{ workTableDialog__table: true, 'flex-column': isXsOnly }"
          >
            <slot
              name="table"
              :data="{ ...$props, search, computedFilters, sortBy, sortDesc }"
              :handle-sort="handleSort"
            >
              <LazyLoadingTable
                ref="table"
                :path="path"
                :headers="headers"
                :search="search"
                :filters="computedFilters"
                :mandatory-filters="mandatoryFilters"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                :get-data="getData"
                :item-class="handleItemClass"
                :add-unique-identifier="addUniqueIdentifier"
                :unique-identifier-key="uniqueIdentifierKey"
                :has-max-height="false"
                @click:row="invokeHandleRowClick"
                @update:options="handleSort"
                @update:lazy-items="handleLazyItems"
              >
                <template
                  v-for="(_, name) in tableContentSlots"
                  :slot="name"
                  slot-scope="slotData"
                >
                  <slot
                    :name="name"
                    v-bind="slotData"
                  />
                </template>
              </LazyLoadingTable>
            </slot>
            <div
              v-if="$slots['append-table-content']"
              :class="{ 'append-table-content': !isXsOnly }"
            >
              <slot name="append-table-content" />
            </div>
          </div>
          <div v-if="$slots['append-table-content-below']">
            <w-container
              justify="center"
              class="workTableDialog__search-filters"
            >
              <slot name="append-table-content-below" />
            </w-container>
          </div>
        </div>
        <w-container
          v-if="$slots['cart']"
          justify="center"
          class="workTableDialog__cart"
        >
          <slot name="cart" />
        </w-container>
      </div>
    </template>
    <template #dialog-actions="{ triggerResolve, triggerReject, close }">
      <slot
        name="dialog-actions"
        :trigger-resolve="triggerResolve"
        :trigger-reject="triggerReject"
        :close="close"
      />
    </template>
  </component>
</template>

<script>
import WorkBasicDialog from '@/components/base_components/dialog/WorkBasicDialog';
import WorkFormDialog from '@/components/base_components/dialog/form_dialogs/WorkFormDialog';
import LazyLoadingTable from '@/components/base_components/tables/LazyLoadingTable';
import WorkDialogSearchFilterWrapper from '@/components/base_components/dialog/components/WorkDialogSearchFilterWrapper';
import { mergeData } from '@/utils/merge-data-util';
import WORK_DIALOG_SIZES from '@/components/base_components/dialog/components/enums/WORK_DIALOG_SIZES';
import mobileFilterMixin from '@/components/base_components/tables/filter/mobile-filter-mixin';
import { filterProps } from '@/utils/functional-component-utils';
import convertSearchHeadersMixixn from '@/mixins/convert-search-headers-mixin';
import { useTimerManager } from '@/composables/timer-manager';

export default {
  name: 'WorkTableDialog',
  components: {
    WorkDialogSearchFilterWrapper,
    LazyLoadingTable,
    WorkFormDialog,
    WorkBasicDialog,
  },
  props: {
    ...WorkFormDialog.props,
    value: { type: [Object, Array], required: false },
    path: { type: String, required: true },
    headers: { type: Array, default: () => [] },
    defaultFilters: { type: Object },
    getData: { type: Function, undefined },
    exposedItems: { type: Array, default: undefined },
    withSearchExpressionFilter: { type: Boolean, default: true },
    handleRowClick: { type: Function },
    handleItemClass: { type: Function, required: false },
    height: { type: String, default: WORK_DIALOG_SIZES.EXTRA_LARGE },
    width: { type: String, default: WORK_DIALOG_SIZES.EXTRA_LARGE },
    addUniqueIdentifier: { type: Boolean, default: false },
    uniqueIdentifierKey: { type: String },
    fixedHeight: { type: Boolean, default: true },
    exludeIds: { type: Array, default: () => [] },
    mandatoryFilters: undefined,
    showSearchFilter: { type: Boolean, default: true },
    isFormDialog: { type: Boolean, default: false },
    showFilter: { type: Boolean, default: true },
    promiseBasedApiRequestHandlerMethod: {
      type: Function,
      default: () => this.value,
    },
    filterPreset: { type: Object },
  },
  mixins: [mobileFilterMixin, convertSearchHeadersMixixn],
  data() {
    return {
      filtersData: { search: '' },
      useFilterPreset: true,
      sortBy: [],
      sortDesc: [],
      items: [],
      collapsePanel: 0,
    };
  },
  setup() {
    const { registerTimeout } = useTimerManager();
    return {
      registerTimeout,
    };
  },
  computed: {
    panelCollapseOnXS() {
      if (this.$vuetify.breakpoint.xsOnly) return;
      else return 0;
    },
    dynamicComponent() {
      return this.isFormDialog ? WorkFormDialog : WorkBasicDialog;
    },
    computedFilters() {
      // eslint-disable-next-line no-unused-vars
      let { search, ...filters } = this.filters;

      if (this.exludeIds && this.exludeIds.length > 0) {
        filters = { ...filters, excludeIds: this.exludeIds.join(',') };
      }

      return mergeData(filters, this.defaultFilters);
    },
    filters: {
      get() {
        return this.filterPreset && this.useFilterPreset
          ? this.filterPreset
          : this.filtersData;
      },
      set(value) {
        this.useFilterPreset = false;
        this.filtersData = value;
        this.$emit('input', value);
      },
    },
    search() {
      return this.convertSearchHeaders(this.filters.search);
    },
    filledActionSlot() {
      return !!this.$scopedSlots['dialog-actions'];
    },
    isXsOnly() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    tableContentSlots() {
      return filterProps(this.$scopedSlots, 'header');
    },
    computedTitle() {
      if (this.simpleTitle) return this.simpleTitle;
      return this.title ? this.title + ' auswählen' : undefined;
    },
  },
  methods: {
    filterProps: filterProps,
    open() {
      this._handleFocusOnOpen();
      return this.$refs.workTableDialog.open();
    },
    close() {
      return this.$refs.workTableDialog.close();
    },
    handleSort($event) {
      this.sortBy = $event.sortBy;
      this.sortDesc = $event.sortDesc;
    },
    handleRowClickDefault(rowItem) {
      this.$refs.workTableDialog.triggerResolve(rowItem);
    },
    handleLazyItems(items) {
      this.$emit('update:exposedItems', items);
    },
    _handleFocusOnOpen() {
      if (this.withSearchExpressionFilter === false) {
        return;
      }
      this.$nextTick(() => {
        this.registerTimeout(() => {
          this?.$refs?.searchExpressionFilter?.$refs?.input?.focus();
        });
      });
    },
    invokeHandleRowClick(rowItem) {
      if (this.handleRowClick === undefined)
        return this.handleRowClickDefault(rowItem);
      else return this.handleRowClick(rowItem);
    },
  },
  watch: {
    filterPreset(newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.useFilterPreset = true;
      }
    },
    panelCollapseOnXS() {
      this.collapsePanel = this.panelCollapseOnXS;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables';

.w-table-dialog__content-wrapper {
  height: 100%;
}
.workTableDialog__cart {
  overflow-y: auto;
  overflow-x: hidden;
  width: 20rem;
}
.workTableDialog__left-prepend {
  overflow-y: auto;
  overflow-x: hidden;
  width: 40rem;
}
.w-table-dialog {
  .w-dialog-content {
    padding: 0 !important;
    height: 100%;
    overflow-y: hidden !important;
    overflow-x: hidden !important;

      .w-table-dialog__table-wrapper {
        height: 100%;
        display: flex;
        flex-direction: column;

        .workTableDialog__search-filters {
          padding: #{$grid-gutter};

          .w-col {
            flex-grow: 1;
          }
        }
        .workTableDialog__table {
          flex: 1;
          overflow: auto;
        }
      }
  }
}

.v-expansion-panels ::v-deep .v-expansion-panel {
  border-radius: $w-unit-radius !important;
  background: none !important;
  margin-top: -1 rem;
}
::v-deep .v-expansion-panels {
  border: $w-unit-border !important;
  border: solid 0.125rem transparent;
  border-radius: 8px;
}
.w-expansion-panel-header {
  padding-bottom: 1rem;
  width: 100%;
  justify-content: flex-start;
  background-color: var(--v-work_card-base);
  overflow: hidden;
}
.w-expansion-panel-content ::v-deep .v-expansion-panel-content__wrap {
  $content-padding: 0;
  background-color: var(--v-work_card-base);
  padding: 4rem 4rem 4rem 1 !important;
  border-bottom-left-radius: $w-unit-radius;
  border-bottom-right-radius: $w-unit-radius;
  margin-top: -2rem !important;
}
.v-expansion-panel-content__wrap > div {
  margin-top: 1rem !important;
  margin-right: 0rem !important;
}
@media #{map-get($display-breakpoints, 'xs-only')} {
  .w-table-dialog .w-dialog-card {
    height: 100vh !important;
  }
}
.table-with-filters {
  max-height: 35rem;
}
.w-dialog .w-dialog-card .w-dialog-content .dialog-content[data-v-5b291f19] .w-unit {
  border-color: transparent !important;
}
</style>